import {convertToURLDatetime, parseStartEndDatetimeFromURLParams} from '@domain/shared'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {SelectedScheduleItemProvider, useCurrentTime} from '@widgets/planning-schedule-chart'
import React, {useCallback, useEffect, useMemo} from 'react'
import {useSearchParams} from 'react-router'

import {PageSkeleton} from '../components'
import {ONE_MINUTE_REFRESH_INTERVAL, OperatorViewLSKeys} from '../consts'
import {LayoutControlOperation} from '../containers'
import {useLocalStorageSettings} from '../hooks'
import {calculateDatetimeRangeWithinBoundary} from '../utils'

const MIN_RANGE_HOURS = 12

export const OperatorViewContainer: React.FC = () => {
  const timezoneId = useUpmPlantTimezone()

  const currentTime = useCurrentTime({
    timezoneId,
    intervalMs: ONE_MINUTE_REFRESH_INTERVAL
  })

  const startOfOperatorView = useMemo(
    () => currentTime.clone().startOf('hour').subtract(1, 'hour'),
    [currentTime]
  )
  const endOfOperatorView = useMemo(
    () => startOfOperatorView.clone().startOf('day').add(8, 'days'),
    [startOfOperatorView]
  )

  const maxSliderRange = useMemo(
    () => endOfOperatorView.diff(startOfOperatorView, 'hours'),
    [startOfOperatorView, endOfOperatorView]
  )

  const [searchParams, setSearchParams] = useSearchParams()

  const obtainedParams = useMemo(
    () => ({
      start: searchParams.get('start'),
      end: searchParams.get('end')
    }),
    [searchParams]
  )

  const parsedSelectedRange = useMemo(() => {
    const parsedRange = parseStartEndDatetimeFromURLParams({...obtainedParams, timezoneId})
    return parsedRange
      ? calculateDatetimeRangeWithinBoundary(parsedRange, [startOfOperatorView, endOfOperatorView])
      : undefined
  }, [obtainedParams, startOfOperatorView, endOfOperatorView, timezoneId])

  const {settings: sliderSettings, updateSettings: updateSliderSetting} = useLocalStorageSettings(
    OperatorViewLSKeys.SliderSettings,
    {defaultRange: 24}
  )

  const updateSliderDefaultRange = useCallback(
    (defaultRange: number) => updateSliderSetting({defaultRange}),
    [updateSliderSetting]
  )
  const sliderDefaultRange = sliderSettings.defaultRange

  useEffect(() => {
    if (!parsedSelectedRange) {
      setSearchParams({
        start: convertToURLDatetime(startOfOperatorView, timezoneId),
        end: convertToURLDatetime(
          startOfOperatorView.clone().add(sliderDefaultRange, 'hours'),
          timezoneId
        )
      })
    }
  }, [parsedSelectedRange, setSearchParams, startOfOperatorView, sliderDefaultRange, timezoneId])

  if (!parsedSelectedRange) {
    return <PageSkeleton />
  }

  return (
    <SelectedScheduleItemProvider>
      <LayoutControlOperation
        updateSliderDefaultRange={updateSliderDefaultRange}
        sliderDefaultRange={sliderSettings.defaultRange}
        sliderMinMaxRange={[MIN_RANGE_HOURS, maxSliderRange]}
        startOfOperatorView={startOfOperatorView}
        endOfOperatorView={endOfOperatorView}
      />
    </SelectedScheduleItemProvider>
  )
}
